import { Controller } from "../../ts/controller";
import { Composer } from "../../ts/models/composer";
import { Game } from "../../ts/models/game";
import { Instrument } from "../../ts/models/instrument";
import { Style } from "../../ts/models/style";
import { User } from "../../ts/models/user";
import { Logger } from "../../ts/utils/logger";
import { AutoCompleteSearch } from "../../ts/web-components/auto-complete-search";
import { AutocompleteSearchModelType } from "../../ts/web-components/auto-complete-search-model-type";

export interface TabsPageInitProps {
    prepopulateStyles: Style[];
    prepopulateInstruments: Instrument[];
    chosenGames: Game[];
    chosenInstruments: Instrument[];
    chosenStyles: Style[];
    chosenComposers: Composer[];
    chosenTabbers: User[];
}

export class TabsPageController extends Controller {
    constructor() {
        super({
            logLabel: 'TabPageController',
        });
    }

    public initialize({
        prepopulateStyles,
        prepopulateInstruments,
        chosenGames,
        chosenInstruments,
        chosenStyles,
        chosenComposers,
        chosenTabbers,
    }: TabsPageInitProps): void {
        const searchGamesElement = this.getElementByIdOrWarn<AutoCompleteSearch<Game>>(
            'tabs-search-games');
        if (searchGamesElement) {
            searchGamesElement.initialize({
                modelType: AutocompleteSearchModelType.game,
                onChange: this._advancedFiltersChanged.bind(this),
            });
            for (let game of chosenGames) {
                searchGamesElement.addItem(game);
            }
        }

        const searchInstrumentsElement = this.getElementByIdOrWarn<AutoCompleteSearch<Instrument>>(
            'tabs-search-instruments');
        if (searchInstrumentsElement) {
            searchInstrumentsElement.initialize({
                modelType: AutocompleteSearchModelType.instrument,
                onChange: this._advancedFiltersChanged.bind(this),
                prepopulatedResults: prepopulateInstruments,
            });
            for (let instrument of chosenInstruments) {
                searchInstrumentsElement.addItem(instrument);
            }
        }

        const searchStylesElement = this.getElementByIdOrWarn<AutoCompleteSearch<Style>>(
            'tabs-search-styles');
        if (searchStylesElement) {
            searchStylesElement.initialize({
                modelType: AutocompleteSearchModelType.style,
                onChange: this._advancedFiltersChanged.bind(this),
                prepopulatedResults: prepopulateStyles,
            });
            for (let style of chosenStyles) {
                searchStylesElement.addItem(style);
            }
        }

        const searchComposersElement = this.getElementByIdOrWarn<AutoCompleteSearch<Composer>>(
            'tabs-search-composers');
        if (searchComposersElement) {
            searchComposersElement.initialize({
                modelType: AutocompleteSearchModelType.composer,
                onChange: this._advancedFiltersChanged.bind(this),
            });
            for (let composer of chosenComposers) {
                searchComposersElement.addItem(composer);
            }
        }

        const searchTabbersElement = this.getElementByIdOrWarn<AutoCompleteSearch<User>>(
            'tabs-search-tabbers');
        if (searchTabbersElement) {
            searchTabbersElement.initialize({
                modelType: AutocompleteSearchModelType.tabber,
                onChange: this._advancedFiltersChanged.bind(this),
            });
            for (let tabber of chosenTabbers) {
                searchTabbersElement.addItem(tabber);
            }
        }

        Logger.info(this.logLabel, 'Initialized');
    }

    private _populateHiddenField({
        dropdownId,
        hiddenFieldId,
    }: {
        dropdownId: string,
        hiddenFieldId: string,
    }): void {
        const dropdown = this.getElementByIdOrWarn<AutoCompleteSearch<any>>(dropdownId);
        const hiddenField = this.getElementByIdOrWarn(hiddenFieldId) as HTMLInputElement;

        if (dropdown && hiddenField) {
            const selectedIds = dropdown.selectedItems.map(item => item.id).join(',');
            hiddenField.value = selectedIds || '';
        }
    }

    private _advancedFiltersChanged(): void {
        this._populateHiddenField({
            dropdownId: 'tabs-search-games',
            hiddenFieldId: 'tabs-advanced-field-games',
        });
        this._populateHiddenField({
            dropdownId: 'tabs-search-instruments',
            hiddenFieldId: 'tabs-advanced-field-instruments',
        });
        this._populateHiddenField({
            dropdownId: 'tabs-search-styles',
            hiddenFieldId: 'tabs-advanced-field-styles',
        });
        this._populateHiddenField({
            dropdownId: 'tabs-search-composers',
            hiddenFieldId: 'tabs-advanced-field-composers',
        });
        this._populateHiddenField({
            dropdownId: 'tabs-search-tabbers',
            hiddenFieldId: 'tabs-advanced-field-users',
        });
    }
}
